import React from 'react'
import { RouteContext } from '../../../../components/route-provider'
import { LIBRARIES_TABS } from '../../helpers/ui-interfaces'
import { LibrariesPageContextValue } from '../../pages'
import { useCLiLibraries, useLibraries } from '../../api/react-query'
import {
  ActiveGridContextType,
  useActiveGridContext
} from '../filters/active-grid-provider'
import { useStaticView } from './useStaticView'
import { useSplitClient } from 'contrastBase/state'

type UseActiveGridArgs = Pick<
  LibrariesPageContextValue,
  'applicationID' | 'gridType'
> & { onSettled?: () => void }

/** Determines the active grid, updates the URL and saves it in context. */
export const useActiveGrid = ({
  gridType,
  applicationID,
  onSettled
}: UseActiveGridArgs) => {
  const { canViewStatic } = useStaticView()
  const { activeGrid, setActiveGrid, initLoadRef } = useActiveGridContext()
  const { isSplitTreatmentOn } = useSplitClient()
  const { organizationID: routeOrganizationId } = React.useContext(RouteContext)

  const organizationID = routeOrganizationId ?? ''
  const isNotApplicationsGrid = gridType !== 'Application'
  const shouldShowCLIGrid = React.useMemo(
    () =>
      isSplitTreatmentOn('PROD-2340_SIR_Projects_Tab') && gridType !== 'Server',
    [gridType, isSplitTreatmentOn]
  )

  // Check if any Runtime libraries exist
  const { data: libraryData } = useLibraries({
    formData: {
      quickFilter: 'ALL',
      apps: applicationID ? [applicationID] : [],
      servers: [],
      tags: [],
      languages: [],
      licenses: [],
      grades: [],
      environments: [],
      severities: [],
      status: [],
      includeUsed: false,
      includeUnused: false,
      q: ''
    },
    keepPreviousData: false,
    organizationID,
    pageIndex: 0,
    pageSize: 1,
    sortingOrder: [
      {
        id: 'grade',
        desc: false
      }
    ],
    enabled: shouldShowCLIGrid,
    onSettled
  })

  // Check if any CLI Libraries exist
  const { data: cliLibraryData } = useCLiLibraries(
    `/Contrast/api/sca/organizations/${organizationID}${
      applicationID ? `/applications/${applicationID}` : ''
    }/libraries?page=0&size=1`,
    shouldShowCLIGrid && isNotApplicationsGrid
  )

  const href = window.location.href

  /** Applies a redirect and ensures params are retained */
  const updateUrl = React.useCallback(
    ({
      tab,
      keepParams
    }: {
      tab?: ActiveGridContextType['activeGrid']
      keepParams?: boolean
    }) => {
      window.location.href = href.replace(
        /(\/libraries)(\/static)*([\S\s]*)/,
        `/libraries${tab ? '/' + tab : ''}${keepParams ? '$3' : ''}`
      )
    },
    [href]
  )

  const updateActiveGrid = React.useCallback(
    (grid: ActiveGridContextType['activeGrid'], keepParams?: boolean) => {
      // Remove query params & update the URL when switching grids
      if (grid === LIBRARIES_TABS.STATIC) {
        updateUrl({ tab: LIBRARIES_TABS.STATIC, keepParams })
        setActiveGrid(LIBRARIES_TABS.STATIC)
      } else if (grid === LIBRARIES_TABS.RUNTIME) {
        updateUrl({ keepParams })
        setActiveGrid(LIBRARIES_TABS.RUNTIME)
      }
    },
    [updateUrl, setActiveGrid]
  )

  React.useEffect(() => {
    // Ensure user has static view permission, otherwise can view runtime only
    if (!canViewStatic && href.includes('libraries/static')) {
      updateActiveGrid(LIBRARIES_TABS.RUNTIME)
    }
  }, [canViewStatic, href, updateActiveGrid])

  React.useEffect(() => {
    if (isNotApplicationsGrid && !activeGrid) {
      updateActiveGrid(
        window.location.href.includes('libraries/static')
          ? LIBRARIES_TABS.STATIC
          : LIBRARIES_TABS.RUNTIME,
        true
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    // On initial load, if there's static libs and no runtime libs, go direct to static grid
    const hasStaticNoRuntime =
      cliLibraryData?.content?.length > 0 &&
      libraryData?.libraries?.length === 0

    // This functionality isn't needed in the Application grid
    if (isNotApplicationsGrid) {
      if (canViewStatic && hasStaticNoRuntime && !initLoadRef.current) {
        updateActiveGrid(LIBRARIES_TABS.STATIC)
        initLoadRef.current = true
      }
    }
  }, [
    initLoadRef,
    updateActiveGrid,
    cliLibraryData?.content?.length,
    libraryData?.libraries?.length,
    isNotApplicationsGrid,
    canViewStatic
  ])

  return {
    activeGrid,
    isStaticGrid: activeGrid === LIBRARIES_TABS.STATIC,
    updateActiveGrid,
    shouldShowCLIGrid
  }
}
